import React, { useState, useEffect } from 'react';
import { Input, Switch, Typography, Card, Avatar, message, Tooltip, Collapse, Badge, Tag } from 'antd';
import { SearchOutlined, LinkOutlined, CheckCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  bgTertiary: '#1a2233',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  success: '#52c41a',
};

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const FiltersContainer = styled(Card)`
  margin-bottom: 24px;
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  
  .ant-card-body {
    padding: 20px !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    .ant-card-body {
      padding: 12px !important;
    }
  }
`;

const StyledCollapse = styled(Collapse)`
  background: transparent !important;
  border: none !important;
  
  .ant-collapse-item {
    border: 1px solid ${COLORS.border} !important;
    border-radius: 8px !important;
    margin-bottom: 12px !important;
    background: ${COLORS.bgSecondary} !important;
    overflow: hidden;
  }
  
  .ant-collapse-header {
    padding: 16px 20px !important;
    color: ${COLORS.textPrimary} !important;
    align-items: center !important;
  }
  
  .ant-collapse-content {
    background: ${COLORS.bgTertiary} !important;
    border-top: 1px solid ${COLORS.border} !important;
  }
  
  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const CurrencyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  
  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
  }
`;

const CurrencyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const CurrencyStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-left: 0;
    width: 100%;
    justify-content: space-between;
    margin-top: 8px;
  }
`;

const ContractRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  padding: 14px 20px;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 12px;
  }
`;

const ContractInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
    flex: 1;
    min-width: 0;
  }
`;

const ContractAddress = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 13px;
  color: ${COLORS.textPrimary};
`;

interface BlockchainNetwork {
  id: string;
  name: string;
  abbreviation?: string;
  iconUrl?: string;
  explorerUrl?: string;
}

interface Contract {
  id: string;
  contractAddress: string;
  contractDecimals?: number;
  isNative?: boolean;
  blockchainNetwork: BlockchainNetwork | null;
  isSupported: boolean;
}

interface CurrencyGroup {
  currency: {
    id: string;
    name: string;
    symbol: string;
    iconUrl?: string;
  };
  contracts: Contract[];
  totalContracts: number;
  supportedContracts: number;
  isFullySupported: boolean;
}

const SupportedContractAddresses: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currencyGroups, setCurrencyGroups] = useState<CurrencyGroup[]>([]);
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId: 1,
        perPage: 500, // Get all grouped by currency
        groupByCurrency: 'true',
      };
      if (search) {
        params.search = search;
      }

      const response = await apiClient.get(API_ENDPOINTS.MERCHANT_GET_SUPPORTED_CONTRACT_ADDRESSES, {
        params,
      });
      
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencyGroups(items);
      }
    } catch (error: any) {
      console.error('Error fetching supported contract addresses:', error);
      message.error('Failed to load supported contract addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleContract = async (contractAddressId: string, isSupported: boolean) => {
    try {
      setTogglingId(contractAddressId);
      const endpoint = API_ENDPOINTS.MERCHANT_TOGGLE_SUPPORTED_CONTRACT_ADDRESS.replace('{contractAddressId}', contractAddressId);
      await apiClient.put(endpoint, {
        isSupported: !isSupported,
      });
      message.success(`Contract ${!isSupported ? 'enabled' : 'disabled'}`);
      fetchContracts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update support status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleAllForCurrency = async (currencyId: string, enable: boolean) => {
    try {
      setTogglingId(currencyId);
      const endpoint = API_ENDPOINTS.MERCHANT_TOGGLE_SUPPORTED_CURRENCY.replace('{currencyId}', currencyId);
      await apiClient.put(endpoint, {
        isSupported: enable,
      });
      message.success(`All contracts ${enable ? 'enabled' : 'disabled'} for this currency`);
      fetchContracts();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update support status');
    } finally {
      setTogglingId(null);
    }
  };

  const getExplorerLink = (contract: Contract) => {
    if (!contract.blockchainNetwork?.explorerUrl || !contract.contractAddress) return null;
    if (contract.isNative || contract.contractAddress.toLowerCase() === 'native') return null;
    
    const baseUrl = contract.blockchainNetwork.explorerUrl.replace(/\/$/, '');
    return `${baseUrl}/address/${contract.contractAddress}`;
  };

  const formatAddress = (address: string) => {
    if (!address || address.toLowerCase() === 'native') return 'Native Token';
    return `${address.substring(0, 10)}...${address.substring(address.length - 8)}`;
  };

  const renderPanelHeader = (group: CurrencyGroup) => (
    <CurrencyHeader>
      {/* Toggle Switch - First for mobile accessibility */}
      <Tooltip title={group.isFullySupported ? 'Disable all contracts' : 'Enable all contracts'}>
        <Switch
          checked={group.isFullySupported}
          loading={togglingId === group.currency.id}
          onChange={(checked, e) => {
            e.stopPropagation();
            handleToggleAllForCurrency(group.currency.id, checked);
          }}
          onClick={(_, e) => e.stopPropagation()}
          size="small"
        />
      </Tooltip>
      
      <CurrencyInfo>
        {group.currency.iconUrl && (
          <Avatar src={group.currency.iconUrl} size={36} />
        )}
        <div>
          <div style={{ fontWeight: 600, fontSize: '15px' }}>{group.currency.name}</div>
          <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
            {group.currency.symbol}
          </div>
        </div>
      </CurrencyInfo>
      
      <CurrencyStats>
        <Badge 
          count={`${group.supportedContracts}/${group.totalContracts}`}
          style={{ 
            backgroundColor: group.isFullySupported ? COLORS.success : COLORS.accent,
            fontSize: '12px',
          }}
        />
      </CurrencyStats>
    </CurrencyHeader>
  );

  const renderContract = (contract: Contract, currencyIconUrl?: string) => {
    const explorerLink = getExplorerLink(contract);
    
    return (
      <ContractRow key={contract.id}>
        {/* Toggle Switch - First for mobile accessibility */}
        <Switch
          checked={contract.isSupported}
          loading={togglingId === contract.id}
          onChange={() => handleToggleContract(contract.id, contract.isSupported)}
          size="small"
        />
        
        <ContractInfo style={{ flex: 1 }}>
          {/* Blockchain Logo */}
          {contract.blockchainNetwork?.iconUrl && (
            <Tooltip title={contract.blockchainNetwork.name}>
              <Avatar 
                src={contract.blockchainNetwork.iconUrl} 
                size={28}
                style={{ border: `1px solid ${COLORS.border}` }}
              />
            </Tooltip>
          )}
          
          {/* Currency Logo (smaller, overlapping) */}
          {currencyIconUrl && (
            <Avatar 
              src={currencyIconUrl} 
              size={20}
              style={{ 
                marginLeft: -8, 
                border: `2px solid ${COLORS.bgTertiary}`,
                zIndex: 1,
              }}
            />
          )}
          
          <div style={{ marginLeft: 8 }}>
            {/* Contract Address */}
            {explorerLink ? (
              <Tooltip title={`View on ${contract.blockchainNetwork?.name || 'explorer'}`}>
                <a 
                  href={explorerLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ 
                    color: COLORS.accent,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    textDecoration: 'none'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ContractAddress>{formatAddress(contract.contractAddress)}</ContractAddress>
                  <LinkOutlined style={{ fontSize: 12 }} />
                </a>
              </Tooltip>
            ) : (
              <Tooltip title={contract.contractAddress !== 'native' ? contract.contractAddress : ''}>
                <ContractAddress>{formatAddress(contract.contractAddress)}</ContractAddress>
              </Tooltip>
            )}
            
            {/* Blockchain Name */}
            <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: 2 }}>
              {contract.blockchainNetwork?.name || 'Unknown Network'}
              {contract.isNative && <Tag style={{ marginLeft: 8, fontSize: '10px' }} color="gold">Native</Tag>}
            </div>
          </div>
        </ContractInfo>
        
        {/* Status icon at the end */}
        {contract.isSupported ? (
          <CheckCircleOutlined style={{ color: COLORS.success, fontSize: 16 }} />
        ) : (
          <MinusCircleOutlined style={{ color: COLORS.textSecondary, fontSize: 16 }} />
        )}
      </ContractRow>
    );
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Supported Contract Addresses
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Select which contract addresses to support for each currency. Each currency may have multiple contracts across different blockchains.
        </Text>
      </HeaderContainer>

      <FiltersContainer>
        <Input
          placeholder="Search by currency name, symbol, or contract address..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 500, width: '100%' }}
          allowClear
        />
      </FiltersContainer>

      <Card
        style={{
          background: 'transparent',
          border: 'none',
        }}
        bodyStyle={{ padding: 0 }}
        loading={loading}
      >
        {currencyGroups.length === 0 && !loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: COLORS.textSecondary }}>
            No currencies with contract addresses found.
          </div>
        ) : (
          <StyledCollapse
            activeKey={activeKeys}
            onChange={(keys) => setActiveKeys(keys as string[])}
            expandIconPosition="start"
          >
            {currencyGroups.map((group) => (
              <Panel 
                key={group.currency.id} 
                header={renderPanelHeader(group)}
              >
                {group.contracts.map((contract) => 
                  renderContract(contract, group.currency.iconUrl)
                )}
              </Panel>
            ))}
          </StyledCollapse>
        )}
      </Card>
    </PageContainer>
  );
};

export default SupportedContractAddresses;
